import { getDoc, getDocs, onSnapshot } from "firebase/firestore";
import { IS_DEV_UI_ENABLED, devLog, devTable, devWarn } from "./devUi";

const DEFAULT_DOC_TTL_MS = 5 * 60 * 1000;
const DEFAULT_QUERY_TTL_MS = 60 * 1000;
const RESOURCE_EXHAUSTED_BACKOFF_MS = 60 * 1000;
const STARTUP_TRACE_MS = 30 * 1000;
const RECENT_READ_LIMIT = 250;
const LARGE_QUERY_WARNING_COUNT = 100;
const ACTION_READ_WARNING_COUNT = 100;
const PAGE_READ_WARNING_COUNT = 250;
const REPEATED_QUERY_WINDOW_MS = 60 * 1000;
const TRIPWIRE_WINDOW_MS = 10 * 60 * 1000;
const TRIPWIRE_READ_LIMIT = 100;
const isDevRuntime = IS_DEV_UI_ENABLED;
const appReadTraceStartedAt = Date.now();

const docCache = new Map();
const queryCache = new Map();
const blockedUntilByKey = new Map();

const getNow = () => Date.now();

const getDebugStack = () => {
  if (!isDevRuntime) return "";
  try {
    return new Error().stack || "";
  } catch {
    return "";
  }
};

const getStartupTrace = () => {
  if (typeof window === "undefined" || !isDevRuntime) return null;

  if (!window.__FQ_STARTUP_READ_TRACE__) {
    window.__FQ_STARTUP_READ_TRACE__ = [];
    window.__FQ_STARTUP_READ_TRACE_STARTED_AT__ = new Date(
      appReadTraceStartedAt
    ).toISOString();
  }

  if (!window.__FQ_STARTUP_READ_TRACE_TIMER__) {
    window.__FQ_STARTUP_READ_TRACE_TIMER__ = window.setTimeout(() => {
      devTable(window.__FQ_STARTUP_READ_TRACE__ || []);
    }, STARTUP_TRACE_MS);
  }

  return window.__FQ_STARTUP_READ_TRACE__;
};

const recordStartupReadTrace = ({
  path,
  readType,
  label = "",
  count = 1,
  cached = false,
  inflight = false,
  reason = "",
  queryDescription = "",
  stack = "",
} = {}) => {
  const elapsed = Date.now() - appReadTraceStartedAt;
  if (elapsed > STARTUP_TRACE_MS) return;

  const trace = getStartupTrace();
  if (!trace) return;

  trace.push({
    timeSinceAppStart: elapsed,
    readType,
    path,
    queryDescription: queryDescription || path,
    callerLabel: label,
    componentOrFunction: label,
    estimatedDocsReturned: Math.max(1, Number(count) || 1),
    reason,
    cacheHit: Boolean(cached),
    inFlightHit: Boolean(inflight),
    stack,
  });
};

export const isFirestoreResourceExhausted = (error) => {
  const code = String(error?.code || "").toLowerCase();
  return code === "resource-exhausted";
};

export const clearFirestoreReadBackoffState = () => {
  blockedUntilByKey.clear();
  return true;
};

const getReadablePath = (refLike, fallback = "unknown") => {
  if (!refLike) return fallback;
  if (typeof refLike === "string") return refLike;
  if (refLike.path) return refLike.path;
  if (refLike._query?.path?.canonicalString) {
    return refLike._query.path.canonicalString();
  }
  return fallback;
};

export const getReadableFirestorePathForDebug = getReadablePath;

const ensureReadMonitor = () => {
  if (typeof window === "undefined") return null;

  if (!window.__FQ_READ_COUNT__) {
    window.__FQ_READ_COUNT__ = {
      total: 0,
      totalReadsEstimated: 0,
      byPath: {},
      byLabel: {},
      readsByMinute: {},
      activeListeners: {},
      byCollection: {},
      byComponent: {},
      startupTrace: window.__FQ_STARTUP_READ_TRACE__ || [],
      recentReads: [],
      repeatedWarnings: {},
      startedAt: new Date().toISOString(),
      lastReadAt: "",
    };
  }

  window.__FQ_READ_COUNT__.total = Number(window.__FQ_READ_COUNT__.total || 0);
  window.__FQ_READ_COUNT__.totalReadsEstimated = Number(
    window.__FQ_READ_COUNT__.totalReadsEstimated ||
      window.__FQ_READ_COUNT__.total ||
      0
  );
  window.__FQ_READ_COUNT__.byPath = window.__FQ_READ_COUNT__.byPath || {};
  window.__FQ_READ_COUNT__.byLabel = window.__FQ_READ_COUNT__.byLabel || {};
  window.__FQ_READ_COUNT__.readsByMinute =
    window.__FQ_READ_COUNT__.readsByMinute || {};
  window.__FQ_READ_COUNT__.activeListeners =
    window.__FQ_READ_COUNT__.activeListeners || {};
  window.__FQ_READ_COUNT__.byCollection =
    window.__FQ_READ_COUNT__.byCollection || {};
  window.__FQ_READ_COUNT__.byComponent =
    window.__FQ_READ_COUNT__.byComponent || {};
  window.__FQ_READ_COUNT__.startupTrace =
    window.__FQ_STARTUP_READ_TRACE__ ||
    window.__FQ_READ_COUNT__.startupTrace ||
    [];
  window.__FQ_READ_COUNT__.recentReads =
    window.__FQ_READ_COUNT__.recentReads || [];
  window.__FQ_READ_COUNT__.repeatedWarnings =
    window.__FQ_READ_COUNT__.repeatedWarnings || {};
  window.__FQ_READ_COUNT__.lastReadAt =
    window.__FQ_READ_COUNT__.lastReadAt || "";

  window.__FQ_READ_MONITOR__ = window.__FQ_READ_COUNT__;
  window.__FQ_READ_SOURCES__ = window.__FQ_READ_SOURCES__ || {};

  if (isDevRuntime && !window.__FQ_READ_COUNT_TABLE_TIMER__) {
    window.__FQ_READ_COUNT_TABLE_TIMER__ = window.setInterval(() => {
      const monitor = window.__FQ_READ_COUNT__;
      if (!monitor?.byLabel) return;
      devTable(monitor.byLabel);
    }, 30000);
  }

  if (isDevRuntime && !window.__FQ_READ_IDLE_SUMMARY_TIMER__) {
    window.__FQ_READ_IDLE_SUMMARY_TIMER__ = window.setTimeout(() => {
      const monitor = window.__FQ_READ_COUNT__ || {};
      const sourceRows = Object.entries(window.__FQ_READ_SOURCES__ || {})
        .map(([key, value]) => ({ key, ...value }))
        .sort((a, b) => Number(b.count || 0) - Number(a.count || 0));
      devWarn("FIRESTORE IDLE 5 MINUTE SUMMARY", {
        totalReads: monitor.totalReadsEstimated || monitor.total || 0,
        topReadSources: sourceRows.slice(0, 20),
        readsPerCollection: monitor.byCollection || {},
        readsPerComponent: monitor.byComponent || {},
        readsByMinute: monitor.readsByMinute || {},
        activeListeners: monitor.activeListeners || {},
      });
    }, 5 * 60 * 1000);
  }

  return window.__FQ_READ_COUNT__;
};

const getTopMonitorEntries = (source = {}, valueSelector = (entry) => entry.reads || 0) =>
  Object.entries(source || {})
    .map(([key, value]) => ({
      key,
      value,
      reads: Number(valueSelector(value) || 0),
    }))
    .sort((a, b) => b.reads - a.reads)
    .slice(0, 10);

const summarizeStack = (stack = "") =>
  String(stack || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(1, 6)
    .join(" | ");

const getSourceFromStack = (stack = "") => {
  const lines = String(stack || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const appLine =
    lines.find(
      (line) =>
        line.includes("src/") ||
        line.includes("src\\") ||
        line.includes("QuotationApp") ||
        line.includes("SuperAdminPanel") ||
        line.includes("AccountContext") ||
        line.includes("globalSearch")
    ) || lines[1] || "";
  const functionMatch = appLine.match(/at\s+([^\s(]+)/);
  const fileMatch = appLine.match(/([^/\\()]+?\.(?:js|jsx|ts|tsx))(?::\d+:\d+)?/);
  return {
    sourceFunction: functionMatch?.[1] || "unknown",
    component: fileMatch?.[1] || appLine || "unknown",
    stackSummary: summarizeStack(stack),
  };
};

const getCollectionFromPath = (path = "") => {
  const cleanPath = String(path || "").split("?")[0];
  const parts = cleanPath.split("/").filter(Boolean);
  if (parts.length >= 4 && parts[0] === "users") return parts[2];
  return parts[parts.length - 1] || cleanPath || "unknown";
};

const ensureReadTripwireOverlay = (monitor) => {
  if (!isDevRuntime || typeof document === "undefined" || !monitor) return;

  const now = Date.now();
  const recentWindowReads = (monitor.recentReads || [])
    .filter((entry) => now - Number(entry.timestampMs || 0) <= TRIPWIRE_WINDOW_MS)
    .reduce((sum, entry) => sum + Number(entry.count || 0), 0);

  if (recentWindowReads <= TRIPWIRE_READ_LIMIT) return;

  const overlayId = "fq-firestore-read-tripwire";
  let overlay = document.getElementById(overlayId);
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = overlayId;
    overlay.style.position = "fixed";
    overlay.style.inset = "16px";
    overlay.style.zIndex = "2147483647";
    overlay.style.maxHeight = "calc(100vh - 32px)";
    overlay.style.overflow = "auto";
    overlay.style.padding = "18px";
    overlay.style.borderRadius = "18px";
    overlay.style.background = "rgba(127, 29, 29, 0.96)";
    overlay.style.color = "#fff";
    overlay.style.fontFamily =
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";
    overlay.style.fontSize = "12px";
    overlay.style.boxShadow = "0 24px 80px rgba(0,0,0,0.38)";
    overlay.style.border = "2px solid rgba(254, 202, 202, 0.85)";
    document.body.appendChild(overlay);
  }

  const topLabels = getTopMonitorEntries(monitor.byLabel, (entry) => entry.reads || 0);
  const topPaths = getTopMonitorEntries(monitor.byPath, (entry) => entry.reads || 0);
  const lastReads = (monitor.recentReads || []).slice(-50).reverse();
  const stackSummary = lastReads
    .map((entry) => ({
      label: entry.label,
      path: entry.path,
      stack: summarizeStack(entry.stack),
    }))
    .filter((entry) => entry.stack)
    .slice(0, 12);

  overlay.innerHTML = `
    <div style="display:flex;justify-content:space-between;gap:16px;align-items:flex-start;margin-bottom:12px;">
      <div>
        <div style="font-size:18px;font-weight:900;">Firestore Read Tripwire</div>
        <div>Session reads: <strong>${Number(monitor.totalReadsEstimated || monitor.total || 0)}</strong></div>
        <div>Reads in last 10 minutes: <strong>${recentWindowReads}</strong></div>
        <div>Route: ${getCurrentRouteForDebug()}</div>
      </div>
      <button id="fq-firestore-read-tripwire-close" style="border:1px solid rgba(255,255,255,.5);background:rgba(255,255,255,.12);color:#fff;border-radius:10px;padding:8px 10px;cursor:pointer;">Close</button>
    </div>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:12px;">
      <div><strong>Top labels</strong><pre style="white-space:pre-wrap;">${JSON.stringify(topLabels, null, 2)}</pre></div>
      <div><strong>Top paths</strong><pre style="white-space:pre-wrap;">${JSON.stringify(topPaths, null, 2)}</pre></div>
      <div><strong>Reads by minute</strong><pre style="white-space:pre-wrap;">${JSON.stringify(monitor.readsByMinute || {}, null, 2)}</pre></div>
    </div>
    <div style="margin-top:12px;"><strong>Last 50 reads</strong><pre style="white-space:pre-wrap;">${JSON.stringify(lastReads, null, 2)}</pre></div>
    <div style="margin-top:12px;"><strong>Stack trace summary</strong><pre style="white-space:pre-wrap;">${JSON.stringify(stackSummary, null, 2)}</pre></div>
  `;

  const closeButton = document.getElementById("fq-firestore-read-tripwire-close");
  if (closeButton) {
    closeButton.onclick = () => overlay.remove();
  }
};

const getCurrentRouteForDebug = () => {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname || "/"}${window.location.search || ""}${
    window.location.hash || ""
  }`;
};

const getReadActionLabel = (label = "") => {
  if (label) return label;
  if (typeof document !== "undefined" && document.activeElement) {
    const active = document.activeElement;
    return (
      active.getAttribute?.("aria-label") ||
      active.textContent?.trim?.().slice(0, 80) ||
      active.id ||
      active.name ||
      ""
    );
  }
  return "";
};

const warnIfReadBudgetExceeded = ({ monitor, path, label, kind, count, stack }) => {
  if (!isDevRuntime || !monitor) return;

  const safeCount = Math.max(1, Number(count) || 1);
  const normalizedPath = String(path || "").toLowerCase();
  const normalizedLabel = String(label || "").toLowerCase();

  if (
    kind === "getDoc" &&
    /users\/[^/]+\/quotations\/[^/?]+/.test(normalizedPath) &&
    !normalizedLabel.includes("fullquoteloadbyid") &&
    !normalizedLabel.includes("save") &&
    !normalizedLabel.includes("readback")
  ) {
    devWarn("FIRESTORE FULL QUOTE LOAD WITHOUT EXPLICIT USER ACTION LABEL", {
      label,
      path,
      route: getCurrentRouteForDebug(),
      stack,
    });
  }

  if (kind !== "getDocs") return;

  if (
    /users\/[^/]+\/quotations(\?|$)/.test(normalizedPath) &&
    !normalizedLabel.includes("repairquotationsearchindex") &&
    !normalizedLabel.includes("rebuildanalyticsindexes") &&
    safeCount > 5
  ) {
    devWarn("FIRESTORE FULL QUOTATIONS COLLECTION READ", {
      label,
      path,
      count: safeCount,
      route: getCurrentRouteForDebug(),
      stack,
    });
  }

  if (normalizedPath.includes("/quote_index") && safeCount > 50) {
    devWarn("FIRESTORE QUOTE INDEX QUERY OVER BUDGET", {
      label,
      path,
      count: safeCount,
      route: getCurrentRouteForDebug(),
      stack,
    });
  }

  if (normalizedPath.includes("/crm_reminders") && safeCount > 30) {
    devWarn("FIRESTORE REMINDER QUERY OVER BUDGET", {
      label,
      path,
      count: safeCount,
      route: getCurrentRouteForDebug(),
      stack,
    });
  }

  const now = Date.now();
  const recentReads = monitor.recentReads || [];
  const recentByLabel = recentReads
    .filter((entry) => entry.label === label && now - Number(entry.timestampMs || 0) < REPEATED_QUERY_WINDOW_MS)
    .reduce((sum, entry) => sum + Number(entry.count || 0), 0);
  const recentByPath = recentReads.filter(
    (entry) => entry.path === path && now - Number(entry.timestampMs || 0) < REPEATED_QUERY_WINDOW_MS
  );
  const startupReads =
    now - appReadTraceStartedAt <= STARTUP_TRACE_MS
      ? Number(monitor.totalReadsEstimated || monitor.total || 0)
      : 0;

  if (safeCount >= LARGE_QUERY_WARNING_COUNT) {
    devWarn("FIRESTORE LARGE QUERY READ", {
      label,
      path,
      count: safeCount,
      route: getCurrentRouteForDebug(),
      stack,
    });
  }

  if (recentByLabel > ACTION_READ_WARNING_COUNT) {
    devWarn("FIRESTORE ACTION READ BUDGET WARNING", {
      label,
      readsLastMinute: recentByLabel,
      path,
      route: getCurrentRouteForDebug(),
    });
  }

  if (startupReads > PAGE_READ_WARNING_COUNT) {
    devWarn("FIRESTORE PAGE LOAD READ BUDGET WARNING", {
      startupReads,
      label,
      path,
      route: getCurrentRouteForDebug(),
    });
  }

  if (recentByPath.length > 1) {
    const warningKey = `${label}::${path}`;
    const lastWarnedAt = Number(monitor.repeatedWarnings?.[warningKey] || 0);
    if (now - lastWarnedAt > REPEATED_QUERY_WINDOW_MS) {
      devWarn("FIRESTORE REPEATED QUERY WARNING", {
        label,
        path,
        runCountLastMinute: recentByPath.length,
        route: getCurrentRouteForDebug(),
      });
      monitor.repeatedWarnings[warningKey] = now;
    }
  }
};

export const recordFirestoreRead = ({
  path,
  label = "",
  kind = "getDoc",
  count = 1,
  cached = false,
  inflight = false,
  ageMs = null,
  stack = "",
} = {}) => {
  const monitor = ensureReadMonitor();
  if (!monitor) return;

  const safePath = path || "unknown";
  const safeCount = Math.max(1, Number(count) || 1);
  const now = Date.now();
  const sourceDetails = getSourceFromStack(stack);
  const collectionName = getCollectionFromPath(safePath);
  const networkReadCount = cached || inflight ? 0 : safeCount;
  const recentEntry = {
    label,
    path: safePath,
    kind,
    count: networkReadCount,
    cacheHit: Boolean(cached),
    inFlightHit: Boolean(inflight),
    timestamp: new Date(now).toISOString(),
    timestampMs: now,
    route: getCurrentRouteForDebug(),
    userAction: getReadActionLabel(label),
    sourceFunction: sourceDetails.sourceFunction,
    component: sourceDetails.component,
    stack,
  };
  monitor.recentReads = [...(monitor.recentReads || []), recentEntry].slice(
    -RECENT_READ_LIMIT
  );

  const minuteKey = new Date(now).toISOString().slice(0, 16);
  const minuteEntry = monitor.readsByMinute[minuteKey] || {
    reads: 0,
    events: 0,
    labels: {},
    paths: {},
  };
  minuteEntry.events += 1;
  minuteEntry.reads += networkReadCount;
  if (label) {
    minuteEntry.labels[label] =
      (minuteEntry.labels[label] || 0) + networkReadCount;
  }
  minuteEntry.paths[safePath] =
    (minuteEntry.paths[safePath] || 0) + networkReadCount;
  monitor.readsByMinute[minuteKey] = minuteEntry;
  const minuteKeys = Object.keys(monitor.readsByMinute || {}).sort();
  minuteKeys.slice(0, Math.max(0, minuteKeys.length - 30)).forEach((key) => {
    delete monitor.readsByMinute[key];
  });

  const entry = monitor.byPath[safePath] || {
    reads: 0,
    cacheHits: 0,
    inflightHits: 0,
    listenerEvents: 0,
    labels: {},
    lastKind: "",
    lastAt: "",
    lastStack: "",
  };

  const sourceKey = `${label || kind || "unlabeled"}::${safePath}`;
  const sourceEntry = window.__FQ_READ_SOURCES__?.[sourceKey] || {
    path: safePath,
    count: 0,
    events: 0,
    lastRead: "",
    sourceFunction: sourceDetails.sourceFunction,
    component: sourceDetails.component,
    label: label || "",
    kind,
    collection: collectionName,
    stackTrace: sourceDetails.stackSummary,
  };
  sourceEntry.events += 1;
  sourceEntry.count += networkReadCount;
  sourceEntry.lastRead = new Date(now).toISOString();
  sourceEntry.sourceFunction = sourceDetails.sourceFunction;
  sourceEntry.component = sourceDetails.component;
  sourceEntry.stackTrace = sourceDetails.stackSummary;
  if (typeof window !== "undefined") {
    window.__FQ_READ_SOURCES__ = window.__FQ_READ_SOURCES__ || {};
    window.__FQ_READ_SOURCES__[sourceKey] = sourceEntry;
  }

  const collectionEntry = monitor.byCollection[collectionName] || {
    reads: 0,
    events: 0,
    paths: {},
    lastAt: "",
  };
  collectionEntry.events += 1;
  collectionEntry.reads += networkReadCount;
  collectionEntry.paths[safePath] = (collectionEntry.paths[safePath] || 0) + networkReadCount;
  collectionEntry.lastAt = new Date(now).toISOString();
  monitor.byCollection[collectionName] = collectionEntry;

  const componentKey = sourceDetails.component || "unknown";
  const componentEntry = monitor.byComponent[componentKey] || {
    reads: 0,
    events: 0,
    labels: {},
    paths: {},
    lastAt: "",
  };
  componentEntry.events += 1;
  componentEntry.reads += networkReadCount;
  if (label) {
    componentEntry.labels[label] = (componentEntry.labels[label] || 0) + networkReadCount;
  }
  componentEntry.paths[safePath] = (componentEntry.paths[safePath] || 0) + networkReadCount;
  componentEntry.lastAt = new Date(now).toISOString();
  monitor.byComponent[componentKey] = componentEntry;

  if (cached) {
    entry.cacheHits += 1;
  } else if (inflight) {
    entry.inflightHits += 1;
  } else {
    entry.reads += safeCount;
    monitor.total += safeCount;
    monitor.totalReadsEstimated = monitor.total;
  }

  if (kind === "onSnapshot") {
    entry.listenerEvents += 1;
  }

  if (label) {
    entry.labels[label] = (entry.labels[label] || 0) + 1;
    const labelEntry = monitor.byLabel[label] || {
      reads: 0,
      cacheHits: 0,
      inflightHits: 0,
      paths: {},
      lastAt: "",
      lastPath: "",
      lastStack: "",
    };
    if (cached) {
      labelEntry.cacheHits += 1;
    } else if (inflight) {
      labelEntry.inflightHits += 1;
    } else {
      labelEntry.reads += safeCount;
    }
    labelEntry.paths[safePath] = (labelEntry.paths[safePath] || 0) + 1;
    labelEntry.lastAt = new Date().toISOString();
    labelEntry.lastPath = safePath;
    labelEntry.lastStack = stack || labelEntry.lastStack || "";
    monitor.byLabel[label] = labelEntry;
  }

  entry.lastKind = kind;
  entry.lastAt = new Date().toISOString();
  entry.lastStack = stack || entry.lastStack || "";
  monitor.lastReadAt = entry.lastAt;
  monitor.startupTrace = window.__FQ_STARTUP_READ_TRACE__ || [];
  monitor.byPath[safePath] = entry;
  recordStartupReadTrace({
    path: safePath,
    readType: kind,
    label,
    count,
    cached,
    inflight,
    reason: cached ? "cache hit" : inflight ? "in-flight dedupe" : "firestore read",
    stack,
  });

  if (!cached && !inflight) {
    warnIfReadBudgetExceeded({
      monitor,
      path: safePath,
      label,
      kind,
      count: safeCount,
      stack,
    });
    ensureReadTripwireOverlay(monitor);
  }

  if (!isDevRuntime) return;

  devLog("Firestore cached read", {
    path: safePath,
    cacheHit: Boolean(cached),
    inFlightHit: Boolean(inflight),
    ageMs,
    label,
    kind,
    stack,
  });

  if (cached) {
    devLog("FIRESTORE CACHE HIT", { path: safePath, label, kind });
    if (kind === "getDocs") {
      devLog("GETDOCS CACHE HIT", { path: safePath, label });
    }
  } else if (inflight) {
    devLog("FIRESTORE INFLIGHT HIT", { path: safePath, label, kind });
    if (kind === "getDocs") {
      devLog("GETDOCS INFLIGHT HIT", { path: safePath, label });
    }
  } else {
    if (kind === "getDocs") {
      devLog("GETDOCS NETWORK READ", {
        path: safePath,
        label,
        count: Math.max(1, Number(count) || 1),
      });
    }
    devWarn(
      "FIRESTORE READ",
      safePath,
      stack,
      new Date().toISOString(),
      {
        label,
        kind,
        count: Math.max(1, Number(count) || 1),
        sourceFunction: sourceDetails.sourceFunction,
        component: sourceDetails.component,
        collection: collectionName,
      }
    );
  }
};

export const recordFirestoreSnapshotRead = (path, label, snap) => {
  recordFirestoreRead({
    path,
    label,
    kind: "onSnapshot",
    count: Number(snap?.size || snap?.docs?.length || 1),
    stack: getDebugStack(),
  });
};

export const trackedGetDoc = async (docRef, options = {}) => {
  const path = options.cacheKey || getReadablePath(docRef);
  const stack = getDebugStack();
  if (isDevRuntime) {
    devLog("FIRESTORE GETDOC START", {
      label: options.label || "",
      path,
      at: new Date().toISOString(),
      stack,
    });
  }
  const snap = await getDoc(docRef);
  recordFirestoreRead({
    path,
    label: options.label,
    kind: "getDoc",
    count: 1,
    stack,
  });
  return snap;
};

export const trackedGetDocs = async (queryRef, options = {}) => {
  const path = options.cacheKey || getReadablePath(queryRef);
  const stack = getDebugStack();
  if (isDevRuntime) {
    devLog("FIRESTORE GETDOCS START", {
      label: options.label || "",
      path,
      at: new Date().toISOString(),
      stack,
    });
  }
  const snap = await getDocs(queryRef);
  recordFirestoreRead({
    path,
    label: options.label,
    kind: "getDocs",
    count: Number(snap?.size || snap?.docs?.length || 1),
    stack,
  });
  return snap;
};

export const trackedOnSnapshot = (target, options = {}, onNext, onError) => {
  const path = options.cacheKey || options.path || getReadablePath(target);
  const label = options.label || "onSnapshot";
  const monitor = ensureReadMonitor();
  const attachStack = getDebugStack();
  const listenerKey = `${label}::${path}`;

  if (monitor) {
    if (isDevRuntime && monitor.activeListeners[listenerKey]) {
      devWarn("FIRESTORE DUPLICATE LISTENER ATTACH", {
        label,
        path,
        previousAttachedAt: monitor.activeListeners[listenerKey].attachedAt,
        previousStack: monitor.activeListeners[listenerKey].stack,
        stack: attachStack,
      });
    }
    monitor.activeListeners[listenerKey] = {
      label,
      path,
      attachedAt: new Date().toISOString(),
      stack: attachStack,
    };
  }

  if (isDevRuntime) {
    devLog("ONSNAPSHOT ATTACHED", {
      path,
      label,
      at: new Date().toISOString(),
      stack: attachStack,
    });
    recordStartupReadTrace({
      path,
      readType: "onSnapshot.attach",
      label,
      count: 1,
      reason: "listener attached",
      stack: attachStack,
    });
  }

  const unsubscribe = onSnapshot(
    target,
    (snap) => {
      recordFirestoreSnapshotRead(path, label, snap);
      onNext(snap);
    },
    onError
  );

  return () => {
    if (monitor?.activeListeners) {
      delete monitor.activeListeners[listenerKey];
    }
    if (isDevRuntime) {
      devLog("ONSNAPSHOT DETACHED", {
        path,
        label,
        at: new Date().toISOString(),
        attachStack,
        cleanupStack: getDebugStack(),
      });
    }
    unsubscribe();
  };
};

export const invalidateFirestoreReadCache = (cacheKeyOrRef) => {
  const key = getReadablePath(cacheKeyOrRef, String(cacheKeyOrRef || ""));
  if (!key) return;
  docCache.delete(key);
  queryCache.delete(key);
  blockedUntilByKey.delete(key);
};

export const invalidateFirestoreReadCacheByPrefix = (cacheKeyPrefix) => {
  const prefix = String(cacheKeyPrefix || "");
  if (!prefix) return;

  [docCache, queryCache].forEach((cache) => {
    Array.from(cache.keys()).forEach((key) => {
      if (String(key).startsWith(prefix)) {
        cache.delete(key);
      }
    });
  });

  Array.from(blockedUntilByKey.keys()).forEach((key) => {
    if (String(key).startsWith(prefix)) {
      blockedUntilByKey.delete(key);
    }
  });

  if (isDevRuntime) {
    devLog("FIRESTORE CACHE PREFIX INVALIDATED", { prefix });
  }
};

const readWithCache = async ({
  refLike,
  cache,
  ttlMs,
  cacheKey,
  label,
  kind,
  reader,
  getCount,
}) => {
  const key = cacheKey || getReadablePath(refLike);
  const now = getNow();
  const existing = cache.get(key);
  const stack = getDebugStack();

  if (existing?.snapshot && now - existing.fetchedAt < ttlMs) {
    recordFirestoreRead({
      path: key,
      label,
      kind,
      cached: true,
      ageMs: now - existing.fetchedAt,
      stack,
    });
    return existing.snapshot;
  }

  if (existing?.promise) {
    recordFirestoreRead({
      path: key,
      label,
      kind,
      inflight: true,
      ageMs: existing.fetchedAt ? now - existing.fetchedAt : null,
      stack,
    });
    return existing.promise;
  }

  const blockedUntil = blockedUntilByKey.get(key) || 0;
  if (blockedUntil > now && existing?.snapshot) {
    recordFirestoreRead({
      path: key,
      label,
      kind,
      cached: true,
      ageMs: now - existing.fetchedAt,
      stack,
    });
    return existing.snapshot;
  }

  if (blockedUntil > now) {
    const error = new Error(
      "Firestore read temporarily paused after resource-exhausted response."
    );
    error.code = "resource-exhausted";
    throw error;
  }

  const promise = reader(refLike)
    .then((snapshot) => {
      recordFirestoreRead({
        path: key,
        label,
        kind,
        count: getCount(snapshot),
        stack,
      });
      // The pending entry is this request's generation token. Invalidation or a
      // newer request replaces it; obsolete results must not resurrect the key.
      if (cache.get(key)?.promise === promise) {
        cache.set(key, { snapshot, fetchedAt: getNow(), promise: null });
      } else {
        const error = new Error("Quotation read superseded by invalidation.");
        error.code = "cancelled";
        throw error;
      }
      return snapshot;
    })
    .catch((error) => {
      if (cache.get(key)?.promise !== promise) throw error;
      if (isFirestoreResourceExhausted(error)) {
        blockedUntilByKey.set(key, getNow() + RESOURCE_EXHAUSTED_BACKOFF_MS);
      }
      cache.delete(key);
      throw error;
    });

  cache.set(key, {
    snapshot: existing?.snapshot || null,
    fetchedAt: existing?.fetchedAt || 0,
    promise,
  });

  return promise;
};

export const getDocCached = (docRef, options = {}) =>
  readWithCache({
    refLike: docRef,
    cache: docCache,
    ttlMs: options.ttlMs ?? DEFAULT_DOC_TTL_MS,
    cacheKey: options.cacheKey,
    label: options.label,
    kind: "getDoc",
    reader: getDoc,
    getCount: () => 1,
  });

export const getDocsCached = (queryRef, options = {}) =>
  readWithCache({
    refLike: queryRef,
    cache: queryCache,
    ttlMs: options.ttlMs ?? DEFAULT_QUERY_TTL_MS,
    cacheKey: options.cacheKey,
    label: options.label,
    kind: "getDocs",
    reader: getDocs,
    getCount: (snapshot) => Number(snapshot?.size || snapshot?.docs?.length || 1),
  });

